import React from 'react';
import { observer } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useTheme } from 'styled-components';
import { BaseText } from '@ui-library/typography';
import { Flex } from '@ui-library/flex';
import { Tooltip } from '@ui-library/tooltip';
import { ModalStep } from '@components/common/multiStepModal';
import { isResourceHidden } from '@components/directory/utils';

import { useAccountManagementStore } from '../../store';
import { SelectableCard } from '../../styles';
import { getAuthTypeIcon } from '../../common/utils';
import { RecommendedBadge } from '../recommendedBadge';
import { DeprecatedBadge } from '../deprecatedBadge';
import { useDesignVariant } from '../../designVariantContext';

export const AccountTypePickerModalStep: React.FC = observer(() => {
  const theme = useTheme();
  const variant = useDesignVariant();
  const { setAccountDefinitionName, selectedAccountDefinition, compatibleAccountDefinitions } =
    useAccountManagementStore();

  return (
    <ModalStep
      gap="16px"
      disabled={!selectedAccountDefinition}
      header={{ title: 'Connect a new account', description: 'Choose an account type' }}
      footer={{ nextStepKey: 'accountTypeForm' }}
    >
      <Flex
        flexDirection="column"
        style={{ overflowY: 'auto', height: '50vh', padding: '2px' }}
        gap=".5rem"
      >
        {compatibleAccountDefinitions
          .filter(
            (accountDefinition) =>
              !isResourceHidden({ options: accountDefinition.displayOptions ?? null }),
          )
          .map((accountDefinition) => {
            const isDeprecated = !!accountDefinition.displayOptions?.deprecated;

            return (
              <Tooltip
                key={accountDefinition.name}
                title={
                  isDeprecated ? accountDefinition.displayOptions?.deprecationLabel : undefined
                }
              >
                <SelectableCard
                  isSelected={selectedAccountDefinition?.name === accountDefinition.name}
                  variant={variant}
                  $disabled={isDeprecated}
                  onClick={() => {
                    if (!isDeprecated) {
                      setAccountDefinitionName(accountDefinition.name);
                    }
                  }}
                >
                  <Flex
                    flexGrow
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItemsCenter
                  >
                    <Flex flexDirection="column" gap="0.25rem">
                      <Flex alignItemsCenter gap="0.25rem">
                        <FontAwesomeIcon
                          icon={getAuthTypeIcon(accountDefinition.uiConfig.icon)}
                          color={variant ? theme.token.next.palette.grey700 : theme.token.colorLink}
                        />

                        <BaseText
                          fontWeight={variant ? 500 : 600}
                          color={variant ? theme.token.next.palette.grey700 : theme.token.colorText}
                        >
                          {accountDefinition.uiConfig.name}
                        </BaseText>
                      </Flex>

                      <BaseText
                        fontWeight={variant ? undefined : 500}
                        color={
                          variant ? theme.token.next.palette.grey500 : theme.token.colorTextTertiary
                        }
                      >
                        {accountDefinition.uiConfig.description}
                      </BaseText>
                    </Flex>

                    {isDeprecated ? (
                      <DeprecatedBadge />
                    ) : (
                      accountDefinition.uiConfig.recommended && <RecommendedBadge />
                    )}
                  </Flex>
                </SelectableCard>
              </Tooltip>
            );
          })}
      </Flex>
    </ModalStep>
  );
});
